import { FormControl, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Info } from 'lucide-react'
import { DebateFormat } from '@db8/shared'
import { useAuth } from '@/contexts/AuthProvider'

export const DebateFormatFormField = ({ field, form }) => {
  const auth = useAuth()

  const options = Object.values(DebateFormat)
    .map((format) => {
      const hasOrg = !!auth.currentOrg
      const isQuickRound = format === 'Quick Round'
      const isLincolnDouglas = format === 'Lincoln Douglas'
      const isPublicForum = format === 'Public Forum'
      const isTeamPolicy = format === 'Team Policy'

      // Quick Round is always available
      if (isQuickRound) {
        return {
          format,
          isEnabled: true,
          label: format,
        }
      }

      // Lincoln Douglas requires an organization (any level)
      if (isLincolnDouglas) {
        const isEnabled = hasOrg
        let labelSuffix = ''
        if (!isEnabled) {
          labelSuffix = ' (Sign up to try)'
        }
        return {
          format,
          isEnabled,
          label: format + labelSuffix,
        }
      }

      // Public Forum and Team Policy
      if (isPublicForum || isTeamPolicy) {
        const isEnabled = hasOrg
        // Always indicate coming soon, but still allow selection when org exists
        const coming = ' (Coming Soon)'
        let labelSuffix = coming
        if (!isEnabled) {
          labelSuffix = ' (Sign up to try)' + coming
        }
        return {
          format,
          isEnabled,
          label: format + labelSuffix,
        }
      }

      // Fallback for any other formats
      return {
        format,
        isEnabled: false,
        label: format + ' (coming soon)',
      }
    })
    .sort((a, b) => {
      if (a.isEnabled === b.isEnabled) return 0
      return a.isEnabled ? -1 : 1
    })

  return (
    <FormItem>
      <div className="flex items-center gap-2 mb-2">
        <FormLabel className="font-semibold">Debate Format</FormLabel>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info size="16px" className="cursor-pointer text-black dark:text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent className="w-72 text-sm text-left">
            <ul className="space-y-2 list-disc list-inside">
              <li>
                <strong>Quick Round:</strong> A fast-paced format with only two rounds per team.
                Available to all users.
              </li>
              <li>
                <strong>Lincoln-Douglas:</strong> A one-on-one debate emphasizing values and ethics.
                Available to users with organizations.
              </li>
              <li>
                <strong>Public Forum:</strong> A format for general audiences emphasizing clarity
                and persuasion. Available to users with organizations.
              </li>
              <li>
                <strong>Team Policy:</strong> Team-based debate focusing on policy analysis and
                strategy. Available to users with organizations.
              </li>
            </ul>
          </TooltipContent>
        </Tooltip>
      </div>

      <Select onValueChange={field.onChange} value={field.value}>
        <FormControl>
          <SelectTrigger>
            <SelectValue placeholder="Select a debate format." />
          </SelectTrigger>
        </FormControl>
        <SelectContent className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0">
          {options.map(({ format, isEnabled, label }) => (
            <SelectItem
              key={format}
              value={format}
              disabled={!isEnabled}
              className={!isEnabled ? 'opacity-50 cursor-not-allowed' : ''}
            >
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <FormMessage />
    </FormItem>
  )
}
